import type { ChatMessage, PatientOption } from '../types/chat'
import { formatChatDateDisplay } from './formatChatDate'

/** French ordinal / position words (voice often uses fr-FR). */
const FR_ORDINAL_PATTERNS = [
  /\b(premier|première|premiere)\b/i,
  /\b(deuxième|deuxieme|second|seconde)\b/i,
  /\b(troisième|troisieme)\b/i,
  /\b(quatrième|quatrieme)\b/i,
  /\b(cinquième|cinquieme)\b/i,
  /\b(sixième|sixieme)\b/i,
  /\b(septième|septieme)\b/i,
  /\b(huitième|huitieme)\b/i,
  /\b(neuvième|neuvieme)\b/i,
  /\b(dixième|dixieme)\b/i,
]

function normalize(text: string): string {
  return text
    .trim()
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\s+/g, ' ')
}

/** Maps spoken ordinal words (EN/FR) to 0-based card index. */
const ORDINAL_WORD_TO_INDEX: Record<string, number> = {
  first: 0,
  second: 1,
  third: 2,
  fourth: 3,
  fifth: 4,
  sixth: 5,
  seventh: 6,
  eighth: 7,
  ninth: 8,
  tenth: 9,
  premier: 0,
  première: 0,
  premiere: 0,
  deuxième: 1,
  deuxieme: 1,
  seconde: 1,
  troisième: 2,
  troisieme: 2,
  quatrième: 3,
  quatrieme: 3,
  cinquième: 4,
  cinquieme: 4,
  sixième: 5,
  sixieme: 5,
  septième: 6,
  septieme: 6,
  huitième: 7,
  huitieme: 7,
  neuvième: 8,
  neuvieme: 8,
  dixième: 9,
  dixieme: 9,
}

function indexFromOrdinalWord(word: string): number | undefined {
  const k = word.toLowerCase()
  return ORDINAL_WORD_TO_INDEX[k]
}

/**
 * Parses phrases like "choose the first option", "open first option", "open the first one", "open second choice",
 * "open the first patient", "second one", "option 2", "deuxième", "2nd".
 * Returns 0-based index, or null if no ordinal intent detected.
 */
export function parseOrdinalChoice(text: string): number | null {
  const t = normalize(text)
  if (!t) return null

  const lower = t.toLowerCase()

  // Avoid obvious negations ("not the first")
  if (/\b(not|no|don'?t|sans)\s+(\w+\s+){0,4}(first|second|third|premier|deuxième|1|2)\b/i.test(lower)) {
    return null
  }

  // Ordinals shared by patterns below (EN + FR)
  const o =
    'first|second|third|fourth|fifth|sixth|seventh|eighth|ninth|tenth|premier|première|premiere|deuxième|deuxieme|second|seconde|troisième|troisieme|quatrième|quatrieme|cinquième|cinquieme|sixième|sixieme|septième|septieme|huitième|huitieme|neuvième|neuvieme|dixième|dixieme'
  const tail =
    'patient|patients|option|options|choice|choices|one|choix|carte|cartes|ligne|lignes|' +
    'location|locations|lieu|lieux|place|places|site|sites'

  // Voice: "choose the first option", "open the first patient", "choisir la deuxième option"
  let pm = lower.match(
    new RegExp(
      `\\b(?:open|ouvrir|select|choose|pick|show|take|display|load|choisir|sélectionner|selectionner)\\s+(?:the\\s+|le\\s+|la\\s+|les\\s+)?(?:the\\s+|le\\s+|la\\s+|les\\s+|l['\u2019]\\s*)?(${o})\\s+(?:${tail})\\b`,
      'i',
    ),
  )
  if (pm) {
    const idx = indexFromOrdinalWord(pm[1])
    if (idx !== undefined) return idx
  }

  // No verb: "first option", "second one", "third choice", "première option"
  pm = lower.match(new RegExp(`\\b(${o})\\s+(?:${tail})\\b`, 'i'))
  if (pm) {
    const idx = indexFromOrdinalWord(pm[1])
    if (idx !== undefined) return idx
  }

  // Explicit: option 2, #3, number 1, n° 2
  let m = lower.match(/\b(?:option|choice|numéro|numero|number|#|n[°o]\s*)\s*(\d{1,3})\b/)
  if (m) {
    const n = parseInt(m[1], 10)
    if (n >= 1 && n <= 999) return n - 1
  }

  m = lower.match(/\b(\d{1,3})(?:st|nd|rd|th)\b/)
  if (m) {
    const n = parseInt(m[1], 10)
    if (n >= 1 && n <= 999) return n - 1
  }

  // Bare single-word ordinals only (avoids "I'm first" when cards are pending)
  const words = lower.split(/\s+/)
  if (words.length === 1) {
    const idx = indexFromOrdinalWord(words[0])
    if (idx !== undefined) return idx
    for (let i = 0; i < FR_ORDINAL_PATTERNS.length; i++) {
      if (FR_ORDINAL_PATTERNS[i].test(lower)) return i
    }
  }

  return null
}

/** Latest assistant message that still shows clinical-document folder option cards. */
export function findPendingFolderOptionsMessage(messages: ChatMessage[]): ChatMessage | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (
      m.role === 'assistant' &&
      m.uiType === 'folder_options' &&
      !m.folderOptionsResolved &&
      m.options &&
      m.options.length > 0
    ) {
      return m
    }
  }
  return undefined
}

/** Latest assistant message that still shows patient option cards (from end of history). */
export function findPendingPatientOptionsMessage(messages: ChatMessage[]): ChatMessage | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (
      m.role === 'assistant' &&
      m.uiType === 'patient_options' &&
      !m.patientOptionsResolved &&
      m.options &&
      m.options.length > 0
    ) {
      return m
    }
  }
  return undefined
}

/**
 * User-visible line after picking a patient card (voice, typed ordinal, or click).
 * Uses the card's name and DOB when available.
 */
export function formatFolderSelectionMessage(
  option: PatientOption | undefined,
  lang: 'en' | 'fr' = 'en',
): string {
  const label = (option?.label || '').replace(/^\d+\.\s*/, '').trim()
  if (label) {
    return lang === 'fr' ? `Document sélectionné : ${label}` : `Selected document: ${label}`
  }
  return lang === 'fr' ? 'Document sélectionné' : 'Selected document'
}

export function formatPatientSelectionMessage(
  option: PatientOption | undefined,
  lang: 'en' | 'fr' = 'en',
): string {
  const label = option?.label?.trim() || ''
  const dobRaw = option?.dob?.trim()
  const dob = dobRaw ? formatChatDateDisplay(dobRaw) || dobRaw : ''
  if (label) {
    if (lang === 'fr') {
      return dob ? `Patient sélectionné : ${label} (né(e) le ${dob})` : `Patient sélectionné : ${label}`
    }
    return dob ? `Selected patient: ${label} (DOB ${dob})` : `Selected patient: ${label}`
  }
  return lang === 'fr' ? 'Patient sélectionné' : 'Selected patient'
}
